import { LineChart } from '@mantine/charts';
import { Card, Group, MultiSelect, Stack, Text } from '@mantine/core';
import { Activity, ChevronDown, Rows3 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { PatientMeasurementRecord } from '../types';
import {
  buildMeasurementChartDataset,
  buildMeasurementChartMetricOptions,
  getDefaultMeasurementChartMetrics,
} from '../utils/measurementHistoryChart';

interface PatientMeasurementsHistoryChartProps {
  history: PatientMeasurementRecord[];
}

export function PatientMeasurementsHistoryChart({
  history,
}: PatientMeasurementsHistoryChartProps) {
  const metricOptions = useMemo(
    () => buildMeasurementChartMetricOptions(history),
    [history]
  );
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(() =>
    getDefaultMeasurementChartMetrics(metricOptions)
  );

  useEffect(() => {
    setSelectedMetrics(current => {
      const validSelection = current.filter(metricKey =>
        metricOptions.some(option => option.value === metricKey)
      );

      if (validSelection.length > 0) {
        return validSelection;
      }

      return getDefaultMeasurementChartMetrics(metricOptions);
    });
  }, [metricOptions]);

  const selectData = useMemo(
    () =>
      metricOptions.map(option => ({
        value: option.value,
        label: option.unit ? `${option.label} (${option.unit})` : option.label,
      })),
    [metricOptions]
  );

  const { data: chartData, series } = useMemo(
    () => buildMeasurementChartDataset(history, selectedMetrics, metricOptions),
    [history, metricOptions, selectedMetrics]
  );

  const segnaliCompactLabel = useMemo(() => {
    if (selectedMetrics.length <= 3) {
      return undefined;
    }

    const visibleLabels = selectedMetrics
      .slice(0, 2)
      .map(value => selectData.find(item => item.value === value)?.label)
      .filter(Boolean);

    const hiddenCount = selectedMetrics.length - visibleLabels.length;

    return `${visibleLabels.join(', ')} (+${hiddenCount} altre)`;
  }, [selectData, selectedMetrics]);

  const shouldCompactSegnaliPills = selectedMetrics.length > 3;

  if (metricOptions.length === 0) {
    return (
      <Card withBorder radius='sm' p='md'>
        <Stack gap='xs' align='center' py='lg'>
          <Activity
            size={40}
            strokeWidth={1.5}
            color='var(--mantine-color-dimmed)'
          />
          <Text size='sm' c='dimmed' ta='center'>
            Registra almeno una rilevazione con valori numerici per vedere
            l&apos;andamento nel tempo.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder radius='sm' p='md'>
      <Stack gap='md'>
        <Group justify='space-between' align='flex-end' wrap='wrap' gap='sm'>
          <Stack gap={2}>
            <Text fw={600}>Andamento rilevazioni</Text>
            <Text size='xs' c='dimmed'>
              Seleziona uno o più segnali da confrontare nello storico.
            </Text>
          </Stack>
          <Stack gap={4} miw={{ base: '100%', xs: 320 }} maw={320}>
            <Text size='sm' fw={500}>
              Segnali
            </Text>
            <MultiSelect
              placeholder={segnaliCompactLabel ?? 'Seleziona misure'}
              variant='filled'
              leftSection={<Rows3 size={14} />}
              rightSection={<ChevronDown size={14} />}
              data={selectData}
              value={selectedMetrics}
              onChange={setSelectedMetrics}
              size='sm'
              radius='sm'
              searchable
              clearable
              maxDropdownHeight={320}
              styles={{
                pill: {
                  display: shouldCompactSegnaliPills ? 'none' : undefined,
                },
                inputField: {
                  minWidth: shouldCompactSegnaliPills ? '100%' : undefined,
                },
              }}
              w='100%'
              aria-label='Seleziona misure da visualizzare nel grafico'
            />
          </Stack>
        </Group>

        {selectedMetrics.length === 0 ? (
          <Text size='sm' c='dimmed' ta='center' py='lg'>
            Seleziona almeno un segnale per visualizzare il grafico.
          </Text>
        ) : chartData.length === 0 ? (
          <Text size='sm' c='dimmed' ta='center' py='lg'>
            Nessun valore numerico disponibile per i segnali selezionati.
          </Text>
        ) : (
          <LineChart
            h={280}
            w='100%'
            withDots
            withTooltip
            data={chartData}
            dataKey='dateLabel'
            curveType='monotone'
            strokeWidth={2}
            series={series}
            yAxisProps={{
              tickFormatter: value =>
                typeof value === 'number' ? value.toFixed(1) : String(value),
            }}
          />
        )}
      </Stack>
    </Card>
  );
}
